<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import qualityIcon from "@/assets/icons/dashboard/quality.svg?raw";
import starIcon from "@/assets/icons/dashboard/star.svg?raw";
import timerIcon from "@/assets/icons/dashboard/timer.svg?raw";
import DashboardStatisticsService from "@/services/dashboardStatisticsService.ts";
import { appConfig } from "@/types/appConfig.type.ts";
import { themeSvgMarkup } from "@/utils/themeSvgUtils.ts";

const footerIcons = {
  timer: themeSvgMarkup(timerIcon, { tintUnknownFills: true }),
  quality: themeSvgMarkup(qualityIcon, { tintUnknownFills: true }),
  star: themeSvgMarkup(starIcon, { tintUnknownFills: true }),
};

const { locale, t } = useI18n();

const activeProductCount = ref<number | null>(null);
const activeManufacturerCount = ref<number | null>(null);

const formatCount = (value: number): string =>
  new Intl.NumberFormat(locale.value).format(value);

const productFooterText = computed<string>(() => {
  if (activeProductCount.value === null) {
    return `${t("common.noValue")} ${t("dashboard.footer.productLabel")}`;
  }

  return `${formatCount(activeProductCount.value)} ${t(
    "dashboard.footer.productLabel",
  )}`;
});

const manufacturerFooterText = computed<string>(() => {
  if (activeManufacturerCount.value === null) {
    return `${t("common.noValue")} ${t("dashboard.footer.manufacturerLabel")}`;
  }

  return `${formatCount(activeManufacturerCount.value)} ${t(
    "dashboard.footer.manufacturerLabel",
  )}`;
});

const loadProductManufacturerActiveCounts = async (): Promise<void> => {
  try {
    const response =
      await DashboardStatisticsService.getProductManufacturerActiveCounts();
    activeProductCount.value = response.active_product_count;
    activeManufacturerCount.value = response.active_manufacturer_count;
  } catch {
    activeProductCount.value = null;
    activeManufacturerCount.value = null;
  }
};

onMounted(async (): Promise<void> => {
  await loadProductManufacturerActiveCounts();
});
</script>

<template>
  <v-footer app class="dashboard-footer">
    <div class="dashboard-footer-content">
      <div class="dashboard-footer-item">
        <span
          class="dashboard-footer-icon"
          aria-hidden="true"
          v-html="footerIcons.timer"
        />
        <p class="dashboard-footer-text">
          <span class="font-weight-bold pr-1">
            {{ t("dashboard.footer.averageCustomer") }}
          </span>
          {{ t("dashboard.footer.averageResponse") }}
        </p>
      </div>

      <div class="dashboard-footer-item">
        <span
          class="dashboard-footer-icon"
          aria-hidden="true"
          v-html="footerIcons.quality"
        />
        <p class="dashboard-footer-text">
          {{ t("dashboard.footer.guarantee") }}
        </p>
      </div>

      <div class="dashboard-footer-item">
        <span
          class="dashboard-footer-icon"
          aria-hidden="true"
          v-html="footerIcons.star"
        />
        <div>
          <p class="dashboard-footer-text font-weight-bold">
            {{ productFooterText }}
          </p>
          <p class="dashboard-footer-text">
            {{ manufacturerFooterText }}
          </p>
        </div>
      </div>

      <div class="dashboard-footer-action">
        <v-btn-secondary
          :href="appConfig.VITE_CALENDLY_URL"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ t("dashboard.footer.button") }}
        </v-btn-secondary>
      </div>
    </div>
  </v-footer>
</template>

<style scoped>
.dashboard-footer-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
}

.dashboard-footer-item {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 14px;
}

.dashboard-footer-icon {
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
}

.dashboard-footer-icon :deep(svg) {
  display: block;
  width: 100%;
  height: 100%;
}

.dashboard-footer-text {
  color: var(--default-font-color);
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  margin: 0;
}

.dashboard-footer-action {
  padding-inline-end: 72px;
}

@media (max-width: 959.98px) {
  .dashboard-footer-content {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .dashboard-footer-item {
    gap: 8px;
  }

  .dashboard-footer-icon {
    width: 28px;
    height: 28px;
  }

  .dashboard-footer-text {
    font-size: 12px;
    line-height: 16px;
  }

  .dashboard-footer-action :deep(.v-btn) {
    max-width: 100%;
    height: auto;
    min-height: 44px;
    padding: 8px 12px;
  }

  .dashboard-footer-action :deep(.v-btn__content) {
    white-space: normal;
  }
}

@media (max-width: 599.98px) {
  .dashboard-footer-action {
    grid-column: 1 / -1;
  }
}
</style>
